import React, {useCallback, useMemo, useState} from 'react';
import {useUserList, userSetActive} from '../../../../../store/user';
import {UserDTO} from 'hemwb-api';
import {userListGridId, useStoredGridState} from '../../../../../store/grid';
import {Link} from 'react-router-dom';
import {getUrlRoute, MRAppRoutes} from '../../../../router/routes';
import {tid} from '../../../../../testUtils';
import {getUserTypeName} from '../../../../../store/user/utils';
import {Search} from '../../../../core/Search/Search';
import AdminSpaceHeader from '../../common/AdminSpaceHeader/AdminSpaceHeader';
import AdminPage from '../../common/AdminPage/AdminPage';
import {
  canActivateUser,
  canChangeUserRole,
  canDeactivateUser,
  canEnrollUser,
} from '../../../../../permissions/usePermissions';
import {Button, Dropdown, Spin, Table, message} from 'antd';
import {
  EditOutlined,
  MailOutlined,
  MessageOutlined,
  MoreOutlined,
  UserOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import type {ColumnsType, TablePaginationConfig} from 'antd/es/table';
import type {SorterResult} from 'antd/es/table/interface';
import type {MenuProps} from 'antd';
import {useUserSetActiveDialog} from '../common/UserSetActiveDialog/useUserSetActiveDialog';
import {useDispatch, useSelector} from 'react-redux';
import {authLoggedInUserSelector} from '../../../../../store/auth';
import styles from './UserList.module.scss';

const searchUsers = (users: UserDTO[], search: string) => {
  if (!search) {
    return users;
  }
  const lcSearch = search.toLocaleLowerCase();
  return users.filter(
    (user) =>
      (user.id || '').toLocaleLowerCase().includes(lcSearch) ||
      (user.email || '').toLocaleLowerCase().includes(lcSearch) ||
      (user.lastName || '').toLocaleLowerCase().includes(lcSearch) ||
      (user.firstName || '').toLocaleLowerCase().includes(lcSearch),
  );
};

const UserList: React.FC = () => {
  const [pagination, setPagination] = useState<{current: number; pageSize: number}>({current: 1, pageSize: 10});
  const [sortBy, setSortBy] = useState<{field: string; order: 'ascend' | 'descend'} | null>(null);
  const gridState = useStoredGridState(userListGridId);
  const list = useUserList();
  const dispatch = useDispatch();
  const loggedInUser = useSelector(authLoggedInUserSelector);
  const {openUserSetActiveDialog, UserSetActiveDialog} = useUserSetActiveDialog();

  const filteredList = useMemo(() => {
    return list && searchUsers(list, gridState.search);
  }, [list, gridState.search]);

  const sortedList = useMemo(() => {
    if (!sortBy || !filteredList) {
      return filteredList;
    }

    const sorted = [...filteredList];
    sorted.sort((a, b) => {
      const field = sortBy.field as keyof UserDTO;
      const aRaw = a[field];
      const bRaw = b[field];

      const aVal = typeof aRaw === 'string' ? aRaw.toLocaleLowerCase() : String(aRaw ?? '');
      const bVal = typeof bRaw === 'string' ? bRaw.toLocaleLowerCase() : String(bRaw ?? '');

      return sortBy.order === 'ascend' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
    return sorted;
  }, [filteredList, sortBy]);

  const handleSearchChange = useCallback(
    (search: string) => {
      gridState.setSearch(search);
      setPagination({current: 1, pageSize: pagination.pageSize});
    },
    [gridState, pagination.pageSize],
  );

  const handleTableChange = (
    pag: TablePaginationConfig,
    _: Record<string, unknown>,
    sorter: SorterResult<UserDTO> | SorterResult<UserDTO>[],
  ) => {
    if (pag.current) {
      setPagination({current: pag.current, pageSize: pag.pageSize || 10});
    }

    if (!Array.isArray(sorter) && sorter.field) {
      setSortBy({
        field: sorter.field as string,
        order: (sorter.order as 'ascend' | 'descend') || 'ascend',
      });
    } else {
      setSortBy(null);
    }
  };

  const handleToggleActive = useCallback(
    (user: UserDTO) => {
      openUserSetActiveDialog({
        user,
        onSubmit: () => userSetActive(dispatch, user.email, !user.active),
      });
    },
    [dispatch, openUserSetActiveDialog],
  );

  const getActionItems = useCallback(
    (user: UserDTO): MenuProps['items'] => {
      const selfUser = loggedInUser?.id === user.id;
      const noEmail = !user.email;
      const items: MenuProps['items'] = [];

      // Only add items if they're enabled (not disabled)
      if (canChangeUserRole(user)) {
        items.push({
          key: 'edit',
          icon: <EditOutlined style={{padding: '10px'}} />,
          label: <Link to={getUrlRoute(MRAppRoutes.USER_EDIT, {id: user.id || ''})}>Edit User</Link>,
        });
      }

      if (canActivateUser(user)) {
        items.push({
          key: 'activate',
          icon: <UserOutlined style={{padding: '10px'}} />,
          label: 'Activate user',
        });
      }

      if (canDeactivateUser(user)) {
        items.push({
          key: 'deactivate',
          icon: <UserOutlined style={{padding: '10px'}} />,
          label: 'Deactivate user',
        });
      }

      if (!noEmail && !selfUser) {
        items.push({
          key: 'email',
          icon: <MailOutlined style={{padding: '10px'}} />,
          label: 'Email User',
        });
      }

      if (!noEmail && !selfUser) {
        items.push({
          key: 'chat',
          icon: <MessageOutlined style={{padding: '10px'}} />,
          label: 'Open Chat',
        });
      }

      return items;
    },
    [loggedInUser?.id],
  );

  const handleOpenChat = useCallback((user: UserDTO) => {
    if (!user?.email) return;

    // Attempt to open XMPP protocol
    const xmppUrl = `xmpp:${user.email}?message`;
    const beforeTime = Date.now();

    // Try to open the xmpp protocol
    window.location.href = xmppUrl;

    // Check if handler exists after a small delay
    // If the protocol handler doesn't exist, the page won't navigate and we'll fall back
    setTimeout(() => {
      // If we're still on the same page after 500ms, the protocol handler likely doesn't exist
      if (Date.now() - beforeTime < 1000) {
        message.error('XMPP chat handler is not configured on this system. Please email the user instead.');
      }
    }, 500);
  }, []);

  const handleActionClick = useCallback(
    (key: string, user: UserDTO) => {
      if (key === 'activate' || key === 'deactivate') {
        handleToggleActive(user);
        return;
      }
      if (key === 'email' && user.email) {
        window.location.href = `mailto:${user.email}`;
        return;
      }
      if (key === 'chat' && user.email) {
        handleOpenChat(user);
      }
    },
    [handleToggleActive],
  );

  const antdColumns: ColumnsType<UserDTO> = useMemo(
    () => [
      {
        title: 'ISID',
        dataIndex: 'id',
        key: 'id',
        sorter: true,
        width: '15%',
      },
      {
        title: 'First Name',
        dataIndex: 'firstName',
        key: 'firstName',
        sorter: true,
        width: '16%',
      },
      {
        title: 'Last Name',
        dataIndex: 'lastName',
        key: 'lastName',
        sorter: true,
        width: '16%',
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        sorter: true,
        width: '24%',
      },
      {
        title: 'Active',
        dataIndex: 'active',
        key: 'active',
        sorter: true,
        width: '10%',
        render: (_, record) => (record.active ? 'Yes' : 'No'),
      },
      {
        title: 'User Type',
        dataIndex: 'userType',
        key: 'userType',
        sorter: true,
        width: '15%',
        render: (_, record) => getUserTypeName(record.userType),
      },
      {
        title: '',
        key: 'actions',
        width: '4%',
        align: 'right' as const,
        render: (_, record) => (
          <Dropdown
            menu={{
              items: getActionItems(record),
              onClick: ({key}) => handleActionClick(String(key), record),
            }}
            trigger={['click']}
            overlayClassName={styles.actionsMenuDropdown}
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              onClick={(event) => event.stopPropagation()}
              {...tid('btn', 'row-actions', String(record.id || ''))}
            />
          </Dropdown>
        ),
      },
    ],
    [getActionItems, handleActionClick],
  );

  const isLoading = !list;

  return (
    <>
      <AdminPage style={{paddingLeft: '2rem'}} header={<AdminSpaceHeader />}>
        <div className={styles.listPanel}>
          <Search value={gridState.search} onValueChange={handleSearchChange} />
          {canEnrollUser() && (
            <Link to={MRAppRoutes.USER_ENROLL}>
              <Button type="primary" size="large" icon={<UserAddOutlined />} {...tid('btn', 'enroll-user')}>
                Enroll new User
              </Button>
            </Link>
          )}
        </div>

        <Spin spinning={isLoading}>
          <div className={styles.recordsContainer}>
            <Table<UserDTO>
              columns={antdColumns}
              dataSource={sortedList || []}
              rowKey={(record) => record.id || ''}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: sortedList?.length || 0,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
              onChange={handleTableChange}
              locale={{emptyText: 'No users were found'}}
              {...tid('user-list-table')}
            />
          </div>
        </Spin>
      </AdminPage>
      {UserSetActiveDialog}
    </>
  );
};

export default UserList;
